import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const PhotoAlbumSection = ({ darkMode }) => {
  const [albumData, setAlbumData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  
  useEffect(() => {
    loadAlbumData();
  }, []);

  const loadAlbumData = async () => {
    try {
      const docRef = doc(db, 'photoAlbumSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setAlbumData(docSnap.data());
      } else {
        // Fallback to default data if no data in Firestore
        setAlbumData({
          subtitle: "Ministry Gallery",
          title: "Ministry in Motion",
          description: "A visual journey of faithful service, leadership, and transformation across Kenya",
          photos: [
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653742/photo_2025-04-02_16-35-19_h7ut9i.webp", 
              caption: "Rev. Peter Kyengo - Lead Minister"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653773/DSC_0514_kmus8u.webp", 
              caption: "Leadership Ministry"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653758/photo_2025-03-16_14-41-58_tto0dv.webp", 
              caption: "Conference Speaking"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653709/photo_2025-05-27_22-36-24_k0kapz.webp", 
              caption: "Church Planting Mission"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653681/shalem__64_t9urqp.webp", 
              caption: "Family Ministry"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653720/photo_2025-04-02_16-35-43_d1fjzu.webp", 
              caption: "Academic Excellence"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763636346/pastors-rd1koyyyyhzzqqg7vahud891ljotap3jyzx4q8facq_ne0vb5.jpg", 
              caption: "Pastoral Leadership"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653174/unnamed_opgua9.jpg", 
              caption: "Ministry Service"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653160/unnamed_ax28l7.jpg", 
              caption: "Worship Leadership"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653211/unnamed_jbarel.jpg", 
              caption: "Teaching Ministry"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653228/unnamed_nzlxp5.jpg", 
              caption: "Evangelistic Outreach"
            },
            { 
              src: "https://res.cloudinary.com/dbacioerk/image/upload/v1763653698/photo_2025-07-02_18-18-55_by6sb2.webp", 
              caption: "FLEC Ministry"
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading photo album section:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Function to automatically assign sizes based on index for beautiful layout
  const getAutoSize = (index, totalPhotos) => {
    // Create a pattern that creates visual interest
    const pattern = [
      'tall', 'medium', 'medium', 'large',
      'medium', 'large', 'tall', 'medium',
      'medium', 'tall', 'large', 'medium'
    ];
    
    // Use pattern or fallback to calculated sizes
    if (pattern[index % pattern.length]) {
      return pattern[index % pattern.length];
    }
    
    // Fallback logic based on index
    if (index % 7 === 0) return 'large';
    if (index % 5 === 0) return 'tall';
    if (index % 3 === 0) return 'medium';
    return 'medium';
  };

  // Function to get CSS classes for each size
  const getSizeClasses = (size) => {
    switch (size) {
      case 'small':
        return 'col-span-1 row-span-1 aspect-square';
      case 'medium':
        return 'col-span-1 row-span-1 md:col-span-1 md:row-span-1 aspect-square';
      case 'large':
        return 'col-span-2 row-span-2 md:col-span-2 md:row-span-2 aspect-square';
      case 'tall':
        return 'col-span-1 row-span-2 md:col-span-1 md:row-span-2 aspect-[3/4]';
      default:
        return 'col-span-1 row-span-1 aspect-square';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'} transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <div className="text-center mb-20 space-y-6">
            <div className={`h-4 bg-slate-400 rounded w-48 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-12 bg-slate-400 rounded w-64 mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
            <div className={`h-8 bg-slate-400 rounded w-96 max-w-full mx-auto ${darkMode ? 'bg-slate-600' : ''}`}></div>
          </div>

          {/* Masonry Grid Skeleton */}
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((_, index) => (
              <div 
                key={index}
                className={`break-inside-avoid rounded-2xl overflow-hidden animate-pulse ${
                  darkMode ? 'bg-slate-700' : 'bg-slate-300'
                } ${
                  index % 7 === 0 ? 'h-80' :
                  index % 5 === 0 ? 'h-64' :
                  index % 3 === 0 ? 'h-48' : 'h-56'
                }`}
              ></div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'} transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto text-center">
          <div className={`${darkMode ? 'bg-slate-800' : 'bg-white'} p-12 rounded-2xl shadow-lg border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className={`text-lg font-bold ${darkMode ? 'text-red-400' : 'text-red-600'} mb-4`}>
              Error Loading Content
            </div>
            <div className={`text-md ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              {error}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Use the actual data from Firestore
  const data = albumData;

  // AlbumPhoto Component
  const AlbumPhoto = ({ photo, index, darkMode, onClick, size }) => {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className={`rounded-2xl overflow-hidden cursor-pointer group relative ${getSizeClasses(size)}`}
        onClick={onClick}
      >
        <motion.img 
          src={photo.src} 
          alt={photo.caption}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          whileHover={{ scale: 1.05 }}
          onError={(e) => {
            // Fallback if image fails to load
            e.target.src = 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653742/photo_2025-04-02_16-35-19_h7ut9i.webp';
          }}
        />
        
        {/* Overlay with caption */}
        <div className={`absolute inset-0 bg-gradient-to-t ${
          darkMode 
            ? 'from-slate-900/80 via-slate-900/20 to-transparent' 
            : 'from-black/60 via-black/20 to-transparent'
        } opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4`}>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            className={`text-sm md:text-base font-semibold ${darkMode ? 'text-white' : 'text-white'} font-sans`}
          >
            {photo.caption}
          </motion.p>
        </div>
      </motion.div>
    );
  };

  return (
    <section className={`py-32 px-8 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'} transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4 font-sans`}>
            {data.subtitle || "Ministry Gallery"}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'} mb-4 font-sans`}>
            {data.title || "Ministry in Motion"}
          </h2>
          <p className={`text-xl ${darkMode ? 'text-slate-400' : 'text-slate-600'} max-w-3xl mx-auto font-sans`}>
            {data.description || "A visual journey of faithful service, leadership, and transformation across Kenya"}
          </p>
        </motion.div>

        {/* Masonry Grid Layout - Automatically arranges photos beautifully */}
        {data.photos && data.photos.length > 0 ? (
          <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {data.photos.map((photo, index) => (
              <div key={index} className="break-inside-avoid">
                <AlbumPhoto 
                  photo={photo} 
                  index={index} 
                  darkMode={darkMode}
                  size={getAutoSize(index, data.photos.length)}
                  onClick={() => setSelectedImage(photo)}
                />
              </div>
            ))}
          </div>
        ) : (
          // Empty state
          <div className="text-center py-16">
            <div className={`text-lg ${darkMode ? 'text-slate-400' : 'text-slate-600'} mb-4`}>
              No photos available in the gallery.
            </div>
            <p className={`${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Add photos using the editor to display them here.
            </p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-pointer"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="relative max-w-6xl w-full max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-slate-300 transition-colors z-10 bg-black/50 rounded-full p-2"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
                <div className="flex-1 overflow-hidden">
                  <img 
                    src={selectedImage.src} 
                    alt={selectedImage.caption}
                    className="w-full h-full max-h-[65vh] object-contain"
                  />
                </div>
                <div className="p-6 bg-white border-t border-slate-200">
                  <p className="text-slate-800 text-center text-lg md:text-xl font-sans font-semibold">
                    {selectedImage.caption}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PhotoAlbumSection;